import { DirectUpload } from "@rails/activestorage"
import { CheckCircle2, Loader2, X } from "lucide-react"
import { useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { cn } from "@/lib/utils"

/**
 * OKS-447 — Direct-upload brief-video field for the mission form. Mirrors
 * the shipped `MasterclassModuleUploadField` (OKS-55): wraps
 * `@rails/activestorage` `DirectUpload`, streams the chosen file to the
 * Rails direct-upload endpoint, and emits a hidden
 * `<input name="mission[brief_video]" value={signedId}>` so the parent
 * Inertia `<Form>` submits the signed_id to the controller's
 * `:brief_video` strong-param. DirectUpload (not a plain multipart file)
 * because brief videos can be large (≤200 MB) — streaming avoids a
 * memory-heavy multipart POST through the Rails cycle.
 *
 * Creator-facing playback is DEFERRED — the helper copy promises it
 * "soon"; this control only authors + stores the video.
 *
 * Crew form rules: plain shadcn primitives; NO shadcn Form / RHF / zod.
 * Server errors render from the parent `<Form>` `errors.brief_video`.
 */
const DEFAULT_DIRECT_UPLOAD_URL = "/rails/active_storage/direct_uploads"

// OKS-447 server allowlist (`MissionBriefVideoBlobValidator`).
const ACCEPTED_MIME = "video/mp4,video/quicktime,video/webm"
// 200 MB — advisory FE pre-check; the server validates blob byte_size
// authoritatively (`Mission::BRIEF_VIDEO_MAX_BYTES`).
const MAX_BYTES = 200 * 1024 * 1024

export type BriefVideoUploadStatus = "idle" | "uploading" | "ready" | "error"

export interface MissionBriefVideoFieldProps {
  /** `name` on the hidden signed_id input — wired into Inertia params. */
  fieldName?: string
  /** Direct-upload endpoint (Rails default). Overridable for testing. */
  directUploadUrl?: string
  /** Initial filename if re-editing a mission with an attached video. */
  initialFilename?: string | null
  /** Initial signed_id if re-editing (keeps the existing blob on save). */
  initialSignedId?: string | null
  /** Submit-gate callback so the parent disables Save while uploading. */
  onStatusChange?: (status: BriefVideoUploadStatus) => void
  /** Inline server error from the parent `errors.brief_video` prop. */
  errorMessage?: string | string[] | null
  /** Disable the picker. */
  disabled?: boolean
  testId?: string
}

interface FileEntry {
  filename: string
  byteSize: number
  status: BriefVideoUploadStatus
  signedId: string | null
  progress: number
  error: string | null
}

export function MissionBriefVideoField({
  fieldName = "mission[brief_video]",
  directUploadUrl = DEFAULT_DIRECT_UPLOAD_URL,
  initialFilename = null,
  initialSignedId = null,
  onStatusChange,
  errorMessage = null,
  disabled = false,
  testId = "mission-brief-video-field",
}: MissionBriefVideoFieldProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const abortRef = useRef<XMLHttpRequest | null>(null)

  const [entry, setEntry] = useState<FileEntry | null>(() => {
    if (initialFilename) {
      return {
        filename: initialFilename,
        byteSize: 0,
        status: "ready",
        signedId: initialSignedId,
        progress: 100,
        error: null,
      }
    }
    return null
  })

  const updateStatus = useCallback(
    (status: BriefVideoUploadStatus) => {
      onStatusChange?.(status)
    },
    [onStatusChange],
  )

  const handleFileSelect = useCallback(
    (file: File) => {
      if (file.size > MAX_BYTES) {
        setEntry({
          filename: file.name,
          byteSize: file.size,
          status: "error",
          signedId: null,
          progress: 0,
          error: t("missions.media.video_error_size"),
        })
        updateStatus("error")
        return
      }

      const baseEntry: FileEntry = {
        filename: file.name,
        byteSize: file.size,
        status: "uploading",
        signedId: null,
        progress: 0,
        error: null,
      }
      setEntry(baseEntry)
      updateStatus("uploading")

      const upload = new DirectUpload(file, directUploadUrl, {
        directUploadWillStoreFileWithXHR(xhr) {
          abortRef.current = xhr
          xhr.upload.addEventListener("progress", (event) => {
            if (!event.lengthComputable) return
            const progress = Math.round((event.loaded / event.total) * 100)
            setEntry((prev) => (prev ? { ...prev, progress } : prev))
          })
        },
      })

      upload.create((error, blob) => {
        abortRef.current = null

        if (error) {
          setEntry((prev) =>
            prev
              ? {
                  ...prev,
                  status: "error",
                  error: t("missions.media.video_error_upload"),
                  progress: 0,
                }
              : prev,
          )
          updateStatus("error")
          return
        }

        setEntry((prev) =>
          prev
            ? {
                ...prev,
                status: "ready",
                signedId: blob.signed_id,
                progress: 100,
                error: null,
              }
            : prev,
        )
        updateStatus("ready")
      })
    },
    [directUploadUrl, t, updateStatus],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) handleFileSelect(file)
      // Reset so re-picking the same filename fires onChange again.
      event.target.value = ""
    },
    [handleFileSelect],
  )

  const handleCancel = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setEntry(null)
    updateStatus("idle")
  }, [updateStatus])

  const handleRemove = useCallback(() => {
    setEntry(null)
    updateStatus("idle")
  }, [updateStatus])

  // When re-editing, a staged removal (entry cleared) must post the
  // remove flag so the server purges the existing blob.
  const removeStaged = Boolean(initialFilename) && entry === null

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_MIME}
        onChange={handleInputChange}
        className="sr-only"
        aria-label={t("missions.media.video_choose_aria")}
        data-testid={`${testId}-input`}
        disabled={disabled}
      />

      {!entry && (
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
            data-testid={`${testId}-choose`}
            className="min-h-11 self-start"
          >
            {t("missions.media.video_upload")}
          </Button>
          <p className="text-muted-foreground text-xs">
            {t("missions.media.video_hint")}
          </p>
          <p className="text-muted-foreground text-xs">
            {t("missions.media.video_watch_soon")}
          </p>
        </div>
      )}

      {entry && (
        <div
          className={cn(
            "flex flex-col gap-2 rounded-md border p-3 text-sm",
            entry.status === "error"
              ? "border-destructive bg-destructive/5"
              : "border-input",
          )}
          data-testid={`${testId}-entry`}
          data-status={entry.status}
        >
          <div className="flex items-center gap-2">
            {entry.status === "uploading" && (
              <Loader2
                className="text-muted-foreground size-4 shrink-0 animate-spin"
                aria-hidden="true"
              />
            )}
            {entry.status === "ready" && (
              <CheckCircle2
                className="size-4 shrink-0 text-emerald-600"
                aria-hidden="true"
              />
            )}
            <span
              className="flex-1 truncate font-medium"
              data-testid={`${testId}-filename`}
            >
              {entry.filename}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={
                entry.status === "uploading" ? handleCancel : handleRemove
              }
              aria-label={
                entry.status === "uploading"
                  ? t("missions.media.video_cancel")
                  : t("missions.media.remove_video")
              }
              data-testid={`${testId}-${entry.status === "uploading" ? "cancel" : "remove"}`}
              // OKS-452 — hit-area only. `size-11` (44px) closes the below
              // 44px touch-target gap (UX-Spec-447 §5); the `X` icon stays
              // `size-3.5` so the row doesn't feel visually bulkier.
              className="size-11 p-0"
            >
              <X className="size-3.5" aria-hidden="true" />
            </Button>
          </div>

          {entry.status === "uploading" && (
            <Progress
              value={entry.progress}
              aria-valuenow={entry.progress}
              role="progressbar"
              data-testid={`${testId}-progress`}
            />
          )}

          {entry.status === "ready" && (
            <p
              className="text-xs text-emerald-700"
              data-testid={`${testId}-ready`}
            >
              {t("missions.media.video_ready")}
            </p>
          )}

          {entry.status === "error" && entry.error && (
            <p
              className="text-destructive text-xs"
              role="alert"
              data-testid={`${testId}-error`}
            >
              {entry.error}
            </p>
          )}

          {/* Keep the existing blob on save (re-edit) OR submit a freshly
              uploaded signed_id. */}
          {entry.status === "ready" && entry.signedId && (
            <input type="hidden" name={fieldName} value={entry.signedId} />
          )}
        </div>
      )}

      {/* Staged removal of a previously-attached video — posts the remove
          flag so the controller purges the blob on save. */}
      {removeStaged ? (
        <input
          type="hidden"
          name="mission[remove_brief_video]"
          value="1"
          data-testid={`${testId}-remove-flag`}
        />
      ) : null}

      {errorMessage && (
        <p
          className="text-destructive text-sm"
          role="alert"
          data-testid={`${testId}-server-error`}
        >
          {Array.isArray(errorMessage) ? errorMessage[0] : errorMessage}
        </p>
      )}
    </div>
  )
}

export default MissionBriefVideoField
